<template>
	<div class="bundle">
		<ClickoutAnchor
			:href="bundleOfferUrl"
			rel="nofollow noopener sponsored"
			target="_blank"
			class="bundle__link"
			clickoutType="regular"
		>
			<ProviderIcon v-for="icon in offer.icons" :key="icon" :iconUrl="icon" class="bundle__icon" />
		</ClickoutAnchor>

		<p class="bundle__label">
			{{ $t('WEBAPP_BUNDLE') }} <span class="bundle__label--presentation">{{ bundleLabelSuffix }}</span>
		</p>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, inject } from 'vue';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { SnowplowContext, SnowplowPackageContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { PresentationType } from '@/@types/graphql-types';
import { OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { OfferLocation } from '@/helpers/offer-helper';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

interface BuyBoxBundleProps {
	title: TitleDetail;
	offer: BuyboxRowOffer;
	isBannerBuyBox: boolean;
	additionalContexts?: SnowplowContext[];
	episodeBuyBoxPresentationType?: BuyboxPresentationType | null;
	isEpisodeBuybox?: boolean;
}
const props = withDefaults(defineProps<BuyBoxBundleProps>(), {
	isBannerBuyBox: false,
	additionalContexts: () => [],
});

const { country } = storeToRefs(useLanguageStore());
const { buyboxPresentationType: regularBuyboxPresentationType } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);

const buyboxPresentationType = computed(() => {
	return props.isEpisodeBuybox ? props.episodeBuyBoxPresentationType : regularBuyboxPresentationType.value;
});

const bundleLabelSuffix = computed(() => {
	switch (buyboxPresentationType.value) {
		case null:
		case PresentationType['4K']:
			return '4k';
		case PresentationType.Hd:
			return 'HD';
		default:
			return '';
	}
});

const packageContext = computed(
	() =>
		new SnowplowPackageContext(
			props.offer.package.packageId,
			props.offer.package.technicalName,
			'bundle',
			props.offer.bundleId,
			props.offer.bundleTechnicalName
		)
);

const bundleOfferUrl = computed(() => {
	const url = new ClickoutUrl(() => ({ ...props.offer, presentationType: props.offer.presentationType }))
		.setTitleSourceId(titleSourceId)
		.set('uct_country', country.value)
		.addContexts(packageContext)
		.addContexts(titleContext)
		.setForTracking(
			'offerLocation',
			props.isBannerBuyBox ? OfferLocation.BANNER_BUYBOX : OfferLocation.REGULAR_BUYBOX
		)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.setCanary(xServeTrack.value);

	// Bypass UCT for US users on bundle amazonstarzamazonhbomax
	if (props.offer.bundleTechnicalName === 'amazonstarzamazonhbomax') {
		url.set('uct_ct', 'bypass');
	}

	return url;
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';
.bundle {
	display: flex;
	flex-direction: column;
	align-items: center;

	&__link {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__icon {
		z-index: 10;

		&:not(:first-of-type) {
			@for $i from 1 through 10 {
				&:nth-child(#{$i}) {
					z-index: #{10 - $i};
				}
			}

			:deep(img.provider-icon.square) {
				width: 34px;
				height: 34px;
				min-width: 34px;
				margin-inline-start: -10px;
			}
		}
	}

	&__label {
		margin: 0;
		text-align: center;
		font-size: 13px;
		text-decoration: none;
		color: $jw-color-lightgray;
		white-space: nowrap;
		&--presentation {
			margin-inline-start: 3px;
			font-size: 9px;
			font-weight: 700;
			color: $jw-color-gold;
			text-transform: uppercase;

			&.bluray {
				color: $jw-color-lightblue;
				text-transform: none;
			}

			&.dvd {
				color: $jw-color-mediumgray;
			}
		}
	}
}
</style>
